// Map.js — إعداد الخريطة والطبقات الأساسية
// ===================================================================
// هذا الملف يحتوي على تهيئة خريطة OpenLayers مع الطبقات التشغيلية
// جميع الإعدادات مستوردة من Config.js للصيانة المركزية
// 
// ⚡ الخريطة والطبقات تعمل بنظام EPSG:28191 (Palestinian Grid)

import OLMap from 'ol/Map';
import View from 'ol/View';
import proj4 from 'proj4';
import { register } from 'ol/proj/proj4';
import { get as getProjection } from 'ol/proj';
import ImageLayer from 'ol/layer/Image';
import ImageWMS from 'ol/source/ImageWMS';
import TileLayer from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS';
import { defaults as defaultControls } from 'ol/control';

// استيراد الإعدادات من ملف التكوين المركزي
import {
  MAP_PROJECTION_CODE,
  MAP_PROJECTION_DEF,
  MAP_CONFIG,
  GEOSERVER_WMS_URL,
  WMS_PARAMS,
  LAYERS,
  GEOMOLG_RESOLUTIONS,
} from './Config';
import Analytics from '../analytics';

// ✅ Advanced Layer Cache for O(1) Lookups
const layerCache = new Map();
window._layerCache = layerCache; // Global debug access

// Initialize Analytics
Analytics.init();

// Initialize Global State Namespace
window.GeoPST = window.GeoPST || {};
window.GeoPST.state = {
  isIdentifyEnabled: false,
  isMeasuring: false,
  isDrawing: false
};

/* ===============================================
   تسجيل نظام الإسقاط الخرائطي EPSG:28191
   =============================================== */
proj4.defs(MAP_PROJECTION_CODE, MAP_PROJECTION_DEF);
register(proj4);

// استخدام EPSG:28191 (Palestinian Grid)
const mapProjection = getProjection(MAP_PROJECTION_CODE);

/* ===============================================
   إنشاء طبقات GeoServer باستخدام ImageWMS
   =============================================== */

// Helper function لإنشاء ImageWMS source (للأداء العالي وسلامة الملصقات/Labels)
const createImageSource = (layerFullName) => new ImageWMS({
  url: GEOSERVER_WMS_URL,
  crossOrigin: 'anonymous',
  params: {
    LAYERS: layerFullName,
    VERSION: WMS_PARAMS.VERSION,
    FORMAT: WMS_PARAMS.FORMAT,
    TRANSPARENT: WMS_PARAMS.TRANSPARENT,
  },
  ratio: 1, // Single image per view for perfect labels
  hidpi: false,
  serverType: 'geoserver',
  // NO imageLoadFunction here -> browser handles <img> natively to external URL
});

// Helper function لإنشاء TileWMS source (فقط للطبقات التي لا تحتوي على نصوص/Labels متأثرة بالقص)
const createTileSource = (layerFullName, format = 'image/png') => new TileWMS({
  url: GEOSERVER_WMS_URL,
  crossOrigin: 'anonymous',
  params: {
    LAYERS: layerFullName,
    VERSION: WMS_PARAMS.VERSION,
    FORMAT: format,
    TRANSPARENT: WMS_PARAMS.TRANSPARENT,
    TILED: true,
  },
  serverType: 'geoserver',
  transition: 0,
});

// طبقة قطع الأراضي (Parcels)
const parcelSource = createImageSource(LAYERS.PARCEL.fullName);
const ParcelLayer = new ImageLayer({
  source: parcelSource,
  name: LAYERS.PARCEL.name,
  visible: LAYERS.PARCEL.visible,
  updateWhileAnimating: false,
  updateWhileInteracting: false,
});
ParcelLayer.set('title', LAYERS.PARCEL.title);
ParcelLayer.set('description', LAYERS.PARCEL.description);
ParcelLayer.set('icon', LAYERS.PARCEL.icon);

// طبقة المباني (Buildings)
const buildingSource = createImageSource(LAYERS.BUILDING.fullName);
const BuildingLayer = new ImageLayer({
  source: buildingSource,
  name: LAYERS.BUILDING.name,
  visible: LAYERS.BUILDING.visible,
  updateWhileAnimating: false,
  updateWhileInteracting: false,
});
BuildingLayer.set('title', LAYERS.BUILDING.title);
BuildingLayer.set('description', LAYERS.BUILDING.description);
BuildingLayer.set('icon', LAYERS.BUILDING.icon);

// طبقة المحافظات (Governorate) - Tile/Cached
const governorateSource = createTileSource(LAYERS.GOVERNORATE.fullName);
const governorateLayer = new TileLayer({
  source: governorateSource,
  name: LAYERS.GOVERNORATE.name,
  visible: LAYERS.GOVERNORATE.visible,
  updateWhileAnimating: false,
  updateWhileInteracting: false,
  transition: 0,
});

// طبقة القرى (Villages) - Tile/Cached
const villageSource = createTileSource(LAYERS.VILLAGE.fullName);
const villageLayer = new TileLayer({
  source: villageSource,
  name: LAYERS.VILLAGE.name,
  visible: LAYERS.VILLAGE.visible,
  updateWhileAnimating: false,
  updateWhileInteracting: false,
  transition: 0,
});

// طبقة المستوطنات (Colonies Border)
const coloniesSource = createImageSource(LAYERS.COLONIES_BORDER.fullName);
const ColoniesBorderLayer = new ImageLayer({
  source: coloniesSource,
  name: LAYERS.COLONIES_BORDER.name,
  visible: LAYERS.COLONIES_BORDER.visible,
  updateWhileAnimating: false,
  updateWhileInteracting: false,
});
ColoniesBorderLayer.set('title', LAYERS.COLONIES_BORDER.title);
ColoniesBorderLayer.set('description', LAYERS.COLONIES_BORDER.description);
ColoniesBorderLayer.set('icon', LAYERS.COLONIES_BORDER.icon);

// طبقة المخطط الهيكلي (Urban Master)
const urbanMasterSource = createImageSource(LAYERS.URBAN_MASTER.fullName);
const UrbanMasterLayer = new ImageLayer({
  source: urbanMasterSource,
  name: LAYERS.URBAN_MASTER.name,
  visible: LAYERS.URBAN_MASTER.visible,
  updateWhileAnimating: false,
  updateWhileInteracting: false,
});
UrbanMasterLayer.set('title', LAYERS.URBAN_MASTER.title);
UrbanMasterLayer.set('description', LAYERS.URBAN_MASTER.description);
UrbanMasterLayer.set('icon', LAYERS.URBAN_MASTER.icon);

// طبقة مناطق ABC
const abcAreasSource = createImageSource(LAYERS.ABC_AREAS.fullName);
const ABCAreasLayer = new ImageLayer({
  source: abcAreasSource,
  name: LAYERS.ABC_AREAS.name,
  visible: LAYERS.ABC_AREAS.visible,
  updateWhileAnimating: false,
  updateWhileInteracting: false,
});
ABCAreasLayer.set('title', LAYERS.ABC_AREAS.title);
ABCAreasLayer.set('description', LAYERS.ABC_AREAS.description);
ABCAreasLayer.set('icon', LAYERS.ABC_AREAS.icon);

// طبقة حدود فلسطين - Tile/Cached
const borderPalestineNearSource = createTileSource(LAYERS.BORDER_PALESTINE_NEAR.fullName);
const BorderPalestineNearLayer = new TileLayer({
  source: borderPalestineNearSource,
  name: LAYERS.BORDER_PALESTINE_NEAR.name,
  visible: LAYERS.BORDER_PALESTINE_NEAR.visible,
  updateWhileAnimating: false,
  updateWhileInteracting: false,
  transition: 0,
});
BorderPalestineNearLayer.set('title', LAYERS.BORDER_PALESTINE_NEAR.title);

// طبقة حدود الضفة وغزة - Tile/Cached
const westBankGazaBorderSource = createTileSource(LAYERS.WESTBANK_GAZA_BORDER.fullName);
const WestBankGazaBorderLayer = new TileLayer({
  source: westBankGazaBorderSource,
  name: LAYERS.WESTBANK_GAZA_BORDER.name,
  visible: LAYERS.WESTBANK_GAZA_BORDER.visible,
  updateWhileAnimating: false,
  updateWhileInteracting: false,
  transition: 0,
});
WestBankGazaBorderLayer.set('title', LAYERS.WESTBANK_GAZA_BORDER.title);

// طبقة الأحواض الطبيعية غير المسجلة
const blocksNonRegisteredSource = createImageSource(LAYERS.BLOCKS_NON_REGISTERED.fullName);
const BlocksNonRegisteredLayer = new ImageLayer({
  source: blocksNonRegisteredSource,
  name: LAYERS.BLOCKS_NON_REGISTERED.name,
  visible: LAYERS.BLOCKS_NON_REGISTERED.visible,
  updateWhileAnimating: false,
  updateWhileInteracting: false,
});
BlocksNonRegisteredLayer.set('title', LAYERS.BLOCKS_NON_REGISTERED.title);
BlocksNonRegisteredLayer.set('description', LAYERS.BLOCKS_NON_REGISTERED.description);
BlocksNonRegisteredLayer.set('icon', LAYERS.BLOCKS_NON_REGISTERED.icon);

// طبقة النقاط المركزية (Centroids) - مخفية من القائمة
const centroidsSource = createImageSource(LAYERS.CENTROIDS.fullName);
const CentroidsLayer = new ImageLayer({
  source: centroidsSource,
  name: LAYERS.CENTROIDS.name,
  visible: LAYERS.CENTROIDS.visible,
  updateWhileAnimating: false,
  updateWhileInteracting: false,
});
CentroidsLayer.set('title', LAYERS.CENTROIDS.title);
CentroidsLayer.set('uiHidden', true); // علامة إضافية للتأكد من إخفائها


/* ===============================================
   استعادة آخر موضع على الخريطة (Session Restore)
   =============================================== */
const SESSION_KEY = 'geopst_map_session';

const _savedSession = (() => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    // Validate that the saved values are sane numbers
    if (
      Array.isArray(s.center) && s.center.length === 2 &&
      isFinite(s.center[0]) && isFinite(s.center[1]) &&
      isFinite(s.zoom) && isFinite(s.rotation)
    ) {
      return s;
    }
    return null;
  } catch { return null; }
})();

/* ===============================================
   إنشاء عرض الخريطة (View) بنظام EPSG:28191
   =============================================== */
const view = new View({
  center: _savedSession ? _savedSession.center : MAP_CONFIG.defaultCenter,
  zoom:   _savedSession ? _savedSession.zoom   : MAP_CONFIG.defaultZoom,
  rotation: _savedSession ? _savedSession.rotation : 0,
  projection: MAP_PROJECTION_CODE,
  extent: [50000, -180000, 250000, 330000],
  maxResolution: GEOMOLG_RESOLUTIONS[0],                          // ⚡ أبعد زوم
  minResolution: 0.0003,                                              // ⚡ أقرب زوم (~1:1)
  constrainOnlyCenter: true,
  smoothExtentConstraint: true,
  // ⚡ Performance for mobile: Disable background loading during movement
  // to prioritize transformation matrix smoothness (60fps)
  loadTilesWhileAnimating: false,
  loadTilesWhilePanning: false,
});

/* ===============================================
   إنشاء كائن الخريطة
   =============================================== */
const map = new OLMap({
  target: 'map',
  layers: [
    // ملاحظة: baseGroup سيُضاف من BasemapSwitcher.js في index 0
    ABCAreasLayer,
    UrbanMasterLayer,
    BlocksNonRegisteredLayer,
    BuildingLayer,
    ParcelLayer,
    governorateLayer,
    villageLayer,
    ColoniesBorderLayer,
    BorderPalestineNearLayer,
    WestBankGazaBorderLayer,
    CentroidsLayer,
  ],
  view,
  controls: defaultControls({ zoom: false, rotate: false }),
  // ⚡ Mobile performance optimizations
  moveTolerance: 8, // Ignore movements smaller than 8px to reduce event noise (jitter filter)
  pixelRatio: 1,    // Stick to 1x pixel ratio on mobile for GPU relief
});

/* ===============================================
   التوافق مع الكود القديم (legacy support)
   =============================================== */
if (typeof $ !== 'undefined') {
  $('#map').data('map', map);
}
window.olMap = map;
window.GeoPST.state.isIdentifyEnabled = false;

/* ===============================================
   تصدير الخريطة والطبقات
   =============================================== */

/* ===============================================
   حفظ موضع الخريطة تلقائياً عند كل حركة
   =============================================== */
map.on('moveend', () => {
  try {
    const center   = view.getCenter();
    const zoom     = view.getZoom();
    const rotation = view.getRotation();
    if (center && isFinite(center[0]) && isFinite(zoom)) {
      localStorage.setItem(SESSION_KEY, JSON.stringify({ center, zoom, rotation }));
    }
  } catch { /* Silently ignore quota errors */ }
});

// ✅ syncLayerCache: Keeps the cache updated when layers are added/removed
function syncLayerCache() {
  layerCache.clear();
  map.getLayers().forEach(layer => {
    const name = layer.get('name');
    if (name) layerCache.set(name, layer);
  });
}

// Watch for changes in the layer collection
map.getLayers().on('add', (e) => {
  const name = e.element.get('name');
  if (name) layerCache.set(name, e.element);
});

map.getLayers().on('remove', (e) => {
  const name = e.element.get('name');
  if (name) layerCache.delete(name);
});

// Initial sync
syncLayerCache();
export { map, layerCache, ParcelLayer, BuildingLayer, UrbanMasterLayer, ColoniesBorderLayer, ABCAreasLayer, CentroidsLayer, BlocksNonRegisteredLayer, mapProjection, view };
export default map;
